import { queryClient } from '../clients/queryClient';
import { queryKeys, EQueryKey } from '../store/queryKeys';

/**
 * Helper for consistent prefetching with staleTime tracking in meta
 */
function prefetchWithStaleTime<T>(
  config: Omit<Parameters<typeof queryClient.prefetchQuery<T>>[0], 'meta'> & {
    staleTime: number;
  },
) {
  return queryClient.prefetchQuery({
    ...config,
    staleTime: config.staleTime,
    meta: { staleTime: config.staleTime },
  });
}

/**
 * Service for managing cache invalidation and synchronization
 * Use this when data is modified to ensure cached data stays up-to-date
 */
class CacheInvalidationService {
  public queryClient = queryClient;
  private pendingInvalidations = new Map<string, NodeJS.Timeout>();
  private batchedInvalidations = new Set<string>();

  async invalidateInspections() {
    await queryClient.invalidateQueries({ queryKey: [EQueryKey.inspections] });
  }

  async invalidateInspection(externalId: string) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.inspection(externalId),
    });
  }

  /**
   * Debounced cache invalidation to prevent excessive rapid invalidations
   * Multiple calls within 300ms will be batched into a single invalidation
   */
  invalidateInspectionAfterObservationChange(inspectionExternalId: string) {
    // Clear any existing timeout for this inspection
    const existingTimeout = this.pendingInvalidations.get(inspectionExternalId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Add to batched invalidations
    this.batchedInvalidations.add(inspectionExternalId);

    // Set new debounced invalidation
    const timeout = setTimeout(async () => {
      try {
        // Process all batched invalidations for this inspection
        if (this.batchedInvalidations.has(inspectionExternalId)) {
          await this.invalidateInspection(inspectionExternalId);
          console.log(
            '✅ Debounced cache invalidation for inspection:',
            inspectionExternalId,
          );

          // Clean up
          this.batchedInvalidations.delete(inspectionExternalId);
          this.pendingInvalidations.delete(inspectionExternalId);
        }
      } catch (error) {
        console.error(
          '❌ Failed to invalidate cache for inspection:',
          inspectionExternalId,
          error,
        );
      }
    }, 300); // 300ms debounce

    this.pendingInvalidations.set(inspectionExternalId, timeout);
  }

  /**
   * Immediate cache invalidation for critical operations
   * Use sparingly - prefer the debounced version for observation changes
   */
  async invalidateInspectionAfterObservationChangeImmediate(
    inspectionExternalId: string,
  ) {
    // Cancel any pending debounced invalidation
    const existingTimeout = this.pendingInvalidations.get(inspectionExternalId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.pendingInvalidations.delete(inspectionExternalId);
    }
    this.batchedInvalidations.delete(inspectionExternalId);

    // Immediate invalidation
    await this.invalidateInspection(inspectionExternalId);
    console.log(
      '✅ Immediate cache invalidation for inspection:',
      inspectionExternalId,
    );
  }

  async invalidateInspectionHistory(assetId: number) {
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.inspectionHistory, assetId],
    });
  }

  async invalidateAllInspectionHistory() {
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.inspectionHistory],
    });
  }

  async invalidateAssets() {
    await queryClient.invalidateQueries({ queryKey: [EQueryKey.assets] });
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.customerSitesAssets],
    });
  }

  async invalidateAsset(assetId: number) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
  }

  async invalidateCustomersAndSites() {
    await queryClient.invalidateQueries({ queryKey: [EQueryKey.customers] });
    await queryClient.invalidateQueries({ queryKey: [EQueryKey.sites] });
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.customerSitesAssets],
    });
  }

  async invalidateMineLocations() {
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.mineLocations],
    });
  }

  async invalidateInspectionAssetEquipment(inspectionId: string) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.inspectionAssetEquipment(inspectionId),
    });
  }

  async invalidateAllInspectionAssetEquipment() {
    await queryClient.invalidateQueries({
      queryKey: [EQueryKey.inspectionAssetEquipment],
    });
  }

  async invalidateStaticData() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [EQueryKey.assetManufacturers],
      }),
      queryClient.invalidateQueries({ queryKey: [EQueryKey.assetModels] }),
      queryClient.invalidateQueries({ queryKey: [EQueryKey.assetTypes] }),
      queryClient.invalidateQueries({ queryKey: [EQueryKey.partTypes] }),
    ]);
  }

  async invalidateAll() {
    await queryClient.invalidateQueries();
  }

  async removeFromCache(queryKey: readonly unknown[]) {
    await queryClient.removeQueries({ queryKey });
  }

  updateCachedData<T>(
    queryKey: readonly unknown[],
    updater: (oldData: T | undefined) => T,
  ) {
    queryClient.setQueryData(queryKey, updater);
  }

  async prefetchInspection(externalId: string) {
    const { inspectionDB } = await import('../data/database.structure');
    const DatabaseInspectionsMethods = (
      await import('../data/database.inspections.methods')
    ).default;

    await prefetchWithStaleTime({
      queryKey: queryKeys.inspection(externalId),
      queryFn: async () => {
        const dbMethods = new DatabaseInspectionsMethods(inspectionDB);
        return await dbMethods.getInspectionByExternalId(externalId);
      },
      staleTime: 10 * 60 * 1000,
      networkMode: 'always',
    });
  }

  async prefetchCustomerSitesAssets(customerId?: number, siteId?: number) {
    const { inspectionDB } = await import('../data/database.structure');

    await prefetchWithStaleTime({
      queryKey: queryKeys.customers(),
      queryFn: async () => {
        return (await inspectionDB.customers.toArray()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      },
      staleTime: 15 * 60 * 1000,
      networkMode: 'always',
    });

    if (customerId) {
      await prefetchWithStaleTime({
        queryKey: queryKeys.sites(customerId),
        queryFn: async () => {
          return await inspectionDB.sites
            .where('customerId')
            .equals(customerId)
            .toArray();
        },
        staleTime: 10 * 60 * 1000,
        networkMode: 'always',
      });
    }

    if (siteId) {
      await prefetchWithStaleTime({
        queryKey: queryKeys.assets(siteId),
        queryFn: async () => {
          return (
            await inspectionDB.assets.where('siteId').equals(siteId).toArray()
          ).sort((a, b) => a.name.localeCompare(b.name));
        },
        staleTime: 8 * 60 * 1000,
        networkMode: 'always',
      });
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    const now = Date.now();

    const getStaleTime = (q: (typeof queries)[number]): number => {
      return typeof q.meta === 'object' && q.meta && 'staleTime' in q.meta
        ? ((q.meta as any).staleTime ?? 0)
        : 0;
    };

    return {
      totalQueries: queries.length,
      freshQueries: queries.filter(
        (q) => q.state.dataUpdatedAt > now - getStaleTime(q),
      ).length,
      staleQueries: queries.filter(
        (q) => q.state.dataUpdatedAt <= now - getStaleTime(q),
      ).length,
      errorQueries: queries.filter((q) => q.state.status === 'error').length,
      loadingQueries: queries.filter((q) => q.state.status === 'pending')
        .length,
    };
  }
}

export const cacheInvalidationService = new CacheInvalidationService();
